/**
 * DeleteAccountCard — patient-facing account deletion flow.
 * Apple App Store 5.1.1(v) + parity with the mobile Delete Account
 * button. Two-gate confirm: password re-entry + type "DELETE".
 * Wipes PII; PHI is retained per HIPAA + state medical-records law.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function DeleteAccountCard() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const canSubmit =
    !!password && confirmText.trim().toUpperCase() === "DELETE" && !submitting;

  const doDelete = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await API.delete("/auth/me", { data: { password } });
      toast.success("Account deleted");
      setOpen(false);
      await logout();
      navigate("/", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete account");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="rounded-2xl border border-red-200 dark:border-red-900/40 bg-white dark:bg-slate-900 p-5"
      data-testid="delete-account-card"
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="rounded-xl bg-red-100 dark:bg-red-900/40 p-2">
          <Trash2 className="w-5 h-5 text-red-600 dark:text-red-300" />
        </div>
        <div className="flex-1">
          <h3 className="font-bold text-slate-900 dark:text-white">Delete account</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Removes your login and personal profile. Active subscriptions are cancelled.{" "}
            <span className="font-semibold">All Protected Health Information (PHI)</span>{" "}
            is stored internally per the laws bound to it (HIPAA and applicable state
            medical-records retention).
          </p>
        </div>
      </div>
      <Button
        variant="destructive"
        onClick={() => { setPassword(""); setConfirmText(""); setOpen(true); }}
        data-testid="open-delete-account-btn"
      >
        <Trash2 className="w-4 h-4 mr-2" /> Delete my account
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="delete-account-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" /> Delete account?
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-700 dark:text-slate-300 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 space-y-2">
            <p>
              This permanently removes your login and personal profile.
              Active subscriptions are cancelled.
            </p>
            <p>
              <span className="font-semibold">All Protected Health Information (PHI)</span>
              {" "}— orders, prescriptions, questionnaires, and medical documents — is
              stored internally per the laws bound to it (HIPAA and applicable state
              medical-records retention, typically 6 years). Your identifying
              information is removed from those records, and they are never sold
              or used for advertising.
            </p>
            <p>You cannot undo this.</p>
          </div>
          <div className="space-y-3 mt-2">
            <div>
              <label className="text-xs font-semibold text-slate-600 dark:text-slate-400 block mb-1">
                Current password
              </label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your current password"
                data-testid="delete-account-password"
                autoComplete="current-password"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 dark:text-slate-400 block mb-1">
                Type DELETE to confirm
              </label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE"
                data-testid="delete-account-confirm"
                autoCapitalize="characters"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={submitting}
              data-testid="delete-account-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={doDelete}
              disabled={!canSubmit}
              data-testid="delete-account-submit-btn"
            >
              {submitting ? "Deleting…" : "Permanently delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
